import React, { Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/utils/formatCurrency";
import { BillingInvoiceDetails } from "../../api/getBillingInvoiceDetails/types";
import BillingInvoiceStatusBadge from "../BillingInvoiceStatusBadge/BillingInvoiceStatusBadge";
import {
  formatBillingDate,
  formatBillingQuantity,
} from "../../utils/billingFormatters";

interface BillingInvoiceDrawerProps {
  open: boolean;
  invoice: BillingInvoiceDetails | null;
  loading: boolean;
  onClose: () => void;
  onDownloadPdf: (id: string) => void;
  downloadingInvoiceId: string | null;
}

const SKELETON_CLASS = "bg-gray-200 dark:bg-slate-700";

const BillingInvoiceDrawer: React.FC<BillingInvoiceDrawerProps> = ({
  open,
  invoice,
  loading,
  onClose,
  onDownloadPdf,
  downloadingInvoiceId,
}) => {
  const details = loading ? null : invoice;

  const totals = details
    ? [
        { label: "Subtotal", value: details.subtotalAmount },
        { label: "Tax", value: details.taxAmount },
        { label: "Total", value: details.totalAmount, emphasized: true },
        { label: "Paid", value: details.paidAmount },
        {
          label: "Balance due",
          value: details.balanceAmount,
          emphasized: true,
        },
      ]
    : [];

  return (
    <Transition as={Fragment} show={open}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto flex w-screen max-w-lg flex-col bg-white dark:bg-slate-900">
                  <div className="flex items-center justify-between bg-primary px-4 py-4">
                    <DialogTitle className="text-lg font-medium text-white">
                      Invoice Details
                    </DialogTitle>
                    <button
                      className="text-white"
                      onClick={onClose}
                      type="button"
                    >
                      <span className="sr-only">Close</span>
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
                    {details ? (
                      <div className="pb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Invoice {details.number}
                          </h3>
                          <BillingInvoiceStatusBadge status={details.status} />
                          <Button
                            className="ml-auto rounded-md"
                            disabled={downloadingInvoiceId !== null}
                            onClick={() => onDownloadPdf(details.id)}
                            size="sm"
                            variant="outline"
                          >
                            {downloadingInvoiceId === details.id && (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                            Download PDF
                          </Button>
                        </div>
                        <dl className="mt-2 space-y-1 text-sm">
                          {[
                            { label: "Issued", value: details.invoiceDate },
                            { label: "Due", value: details.dueDate },
                          ].map(({ label, value }) => (
                            <div className="flex" key={label}>
                              <dt className="w-16 text-gray-500 dark:text-gray-400">
                                {label}
                              </dt>
                              <dd className="font-medium text-gray-900 dark:text-white">
                                {formatBillingDate(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ) : (
                      <div className="pb-2">
                        <span className="sr-only">Loading invoice details</span>
                        <div className="flex items-center gap-2">
                          <Skeleton className={`h-7 w-44 ${SKELETON_CLASS}`} />
                          <Skeleton
                            className={`h-5 w-12 rounded-full ${SKELETON_CLASS}`}
                          />
                        </div>
                        <div className="mt-2 space-y-1">
                          {[0, 1].map((index) => (
                            <div className="flex items-center" key={index}>
                              <Skeleton
                                className={`h-5 w-12 ${SKELETON_CLASS}`}
                              />
                              <Skeleton
                                className={`ml-4 h-5 w-24 ${SKELETON_CLASS}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="min-h-0 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-700">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500 dark:bg-slate-800 dark:text-gray-400">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">
                              Description
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                              Qty
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                              Unit price
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                          {!details &&
                            [0, 1, 2].map((index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">
                                  <Skeleton
                                    className={`h-5 w-40 ${SKELETON_CLASS}`}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <Skeleton
                                    className={`ml-auto h-5 w-4 ${SKELETON_CLASS}`}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <Skeleton
                                    className={`ml-auto h-5 w-16 ${SKELETON_CLASS}`}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <Skeleton
                                    className={`ml-auto h-5 w-16 ${SKELETON_CLASS}`}
                                  />
                                </td>
                              </tr>
                            ))}
                          {details?.lines.length === 0 && (
                            <tr>
                              <td
                                className="px-3 py-6 text-center text-gray-500 dark:text-gray-400"
                                colSpan={4}
                              >
                                No line items on this invoice
                              </td>
                            </tr>
                          )}
                          {details?.lines.map((line, index) => (
                            <tr
                              className="text-gray-900 dark:text-gray-100"
                              key={`${line.description}-${index}`}
                            >
                              <td className="px-3 py-2">{line.description}</td>
                              <td className="px-3 py-2 text-right">
                                {formatBillingQuantity(line.quantity)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCurrency(line.unitPrice)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCurrency(line.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <dl className="space-y-1.5 border-t border-gray-200 pt-4 text-sm dark:border-slate-700">
                      {!details
                        ? [0, 1, 2, 3, 4].map((index) => (
                            <div className="flex justify-between" key={index}>
                              <Skeleton
                                className={`h-5 w-16 ${SKELETON_CLASS}`}
                              />
                              <Skeleton
                                className={`h-5 w-20 ${SKELETON_CLASS}`}
                              />
                            </div>
                          ))
                        : totals.map(({ label, value, emphasized }) => (
                            <div className="flex justify-between" key={label}>
                              <dt
                                className={
                                  emphasized
                                    ? "font-medium text-gray-900 dark:text-white"
                                    : "text-gray-500 dark:text-gray-400"
                                }
                              >
                                {label}
                              </dt>
                              <dd
                                className={`text-gray-900 dark:text-white ${
                                  emphasized ? "font-semibold" : ""
                                }`}
                              >
                                {formatCurrency(value)}
                              </dd>
                            </div>
                          ))}
                    </dl>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BillingInvoiceDrawer;
